/**
 * Types for the per-dealer config-settings panel (Epic 7.1.4 Phase 4b).
 *
 * Verified against the merged `dealers-service` backend (PRs #679, #682):
 * - `backend/dealers-service/app/Http/Controllers/DealerConfigOptionController.php`
 * - `backend/dealers-service/app/Http/Controllers/DealerConfigSettingController.php`
 * - `packages/common-package/src/App/Models/ConfigOption.php` / `ConfigSetting.php`
 *
 * Mirrors `www/src/features/products/types/products.types.ts` (`MoneyJson`,
 * `ConfigSetting`), adapted to the dealer catalog's own shape rather than
 * re-imported (see plan §"Reuse, do not re-import").
 *
 * The config-options CATALOG types (`DealerConfigOption`,
 * `DealerConfigOptionsPage`, `DealerConfigOptionListParams`,
 * `extractDealerConfigOptionsMeta`) live in the canonical
 * `./dealerConfigOption.types` (Epic 7.1.4 P4a) — this file only imports
 * `DealerConfigOption` for the `DealerConfigSetting.configOption` reference
 * and does not redefine the catalog types (GH #697).
 */

// Schema file, not the barrel: the barrel's components reach useAuth -> dealerConfigSlice
// -> back to this file, a circular import.
import { CONFIG_DATA_TYPES } from '@/shared/components/config-settings/configSettingAddSchema';
import type { DealerConfigOption } from './dealerConfigOption.types';

// Mirrors packages/common-package MoneyResource JSON shape.
export interface MoneyJson {
    amount: string; // canonical 2dp decimal string, e.g. "50.00"
    currency: string; // ISO 4217, e.g. "GBP"
    minor: number; // integer minor units, e.g. 5000
}

export const DEALER_CONFIG_DATA_TYPES = CONFIG_DATA_TYPES;

export type DealerConfigDataType = (typeof DEALER_CONFIG_DATA_TYPES)[number];

export type DealerConfigSettingValue = string | number | boolean | string[] | MoneyJson;

/** A per-dealer config value (`dealer_config_settings`). */
export interface DealerConfigSetting {
    id: string;
    dealer_id: string;
    config_option_id?: string | null;
    key: string;
    value: DealerConfigSettingValue;
    configOption?: DealerConfigOption | null;
    created_at?: string;
    updated_at?: string;

    // 7.1.6 Slice 1 provenance — present only on the
    // `?with=effective_config_settings` path, hence optional. snake_case on
    // purpose, so the existing normaliser needs no change.
    inherited?: boolean;
    source_dealer_id?: string;
    source_dealer_name?: string;
}

/**
 * `GET /dealers/{dealer}/config-settings` response shape (load-bearing —
 * verified against `DealerConfigSettingController::configSettings()`).
 * NOT a paginator: `{ data: [...] }` only, with an optional caller-supplied
 * `limit` (max 100, default 100). Do not conflate with `DealerConfigOptionsPage`.
 */
export interface DealerConfigSettingsResponse {
    data: DealerConfigSetting[];
}

export interface DealerConfigSettingsListParams {
    /** 1-100; backend defaults to 100 when omitted. */
    limit?: number;
}

export interface AttachDealerConfigSettingPayload {
    key: string;
    value: DealerConfigSettingValue;
    config_option_id?: string | null;
}

export interface UpdateDealerConfigSettingPayload {
    value: DealerConfigSettingValue;
    config_option_id?: string | null;
}

export interface UpdateDealerConfigSettingByIdPayload {
    value: DealerConfigSettingValue;
}

export interface ApplyDealerConfigCatalogPayload {
    group?: string;
    defaults: Record<string, DealerConfigSettingValue>;
}

export interface ApplyDealerConfigCatalogResult {
    created_count: number;
    skipped_count: number;
    skipped_keys: string[];
    data: DealerConfigSetting[];
}

/**
 * The dealer config key governing mileage pre-population in the quote journey
 * (#789). Declared once here so the seeder key, the resolver and the tests
 * cannot drift. MUST match DealerConfigOptionSeeder's `key_name` exactly.
 */
export const PREPOPULATE_MILEAGE_KEY = 'prepopulate_mot_mileage_estimates';

/**
 * The dealer config key governing quote-journey step order. MUST match
 * DealerConfigOptionSeeder's `key_name`.
 */
export const DEFER_DEALER_SELECTION_KEY = 'defer_dealer_selection';

/** Resolved for the quote's SELECTED dealer, not the user's home dealer. MUST
 *  match DealerConfigOptionSeeder's `key_name` exactly. */
export const CAN_SELF_COLLECT_PAYMENT_KEY = 'can_self_collect_payment';

/**
 * Upper bound on the ambient dealer-config read, enforced twice: as an Axios
 * `timeout` that aborts the request, and as a race in the thunk that guarantees it settles.
 */
export const DEALER_CONFIG_FETCH_TIMEOUT_MS = 10_000;

/** Per-dealer fetch status for the ambient dealer-config cache. */
export type DealerConfigStatus = 'idle' | 'loading' | 'ready' | 'error';
